/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  FC,
  memo,
  useEffect,
  useImperativeHandle,
} from 'react';

import Radio, { RadioChangeEvent } from 'antd/es/radio';

import { useSkipEffectOnMount } from '@hooks/lifecycle';

import { RadioGroupContainer, RadioGroupStyleProps } from './styled';

export type ResetSelectionFNRef = React.Ref<() => void>;

export type RadioGroupProps<T = any> = {
  /** Function called when an option is selected.
   * if `getOption` props is passed, its return value will be passed
   * to `onChange`, otherwise the return value of `getValue` props
   */
  onChange?: <Key>(value: Key | T, index?: number) => void;
  /** the value selected by default */
  selectedIndex?: number;
  /** Array of options */
  options?: T[];
  /** Function to compute the label to be displayed for complex option types */
  getLabel?: (option: T) => React.ReactNode;
  /** Function to compute the key value associated to an option*/
  getValue?: (option: T, index?: number) => string;
  /**Function to compute an option from the key */
  getOption?: (key: string) => [T, number];
  disabled?: boolean;
  renderRadioElement?: (
    radioElement: React.ReactNode,
    option: T,
    key: unknown
  ) => React.ReactNode;
  resetSelectionFnRef?: ResetSelectionFNRef;
} & Partial<RadioGroupStyleProps>;

const MIN_SHIFT = -3; // help in resetting the selected index

const UseReset: FC<{
  resetRef: ResetSelectionFNRef;
  selectIndex: React.Dispatch<React.SetStateAction<number>>;
}> = ({ selectIndex, resetRef }) => {
  useImperativeHandle(
    resetRef,
    () => () => selectIndex((x) => (x >= 0 ? -1 : MIN_SHIFT - x)),
    [selectIndex]
  );
  return null;
};

export const RadioGroup: FC<RadioGroupProps> = ({
  onChange,
  selectedIndex: initialSelectedIndex = -1,
  options,
  getLabel = (x) => x,
  getValue = (x) => x as string,
  getOption = (x) => [x, -1],
  selectedOptionColor = 'primaryBrand',
  color,
  direction = 'row',
  disabled,
  renderRadioElement = (radioElement) => radioElement,
  resetSelectionFnRef,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex);
  const [selectedValue, setSelectedValue] = useState(() => {
    const option = options?.[selectedIndex] ?? undefined;
    return option ? getValue(option, selectedIndex) : undefined;
  });
  const updateInternal = (e: RadioChangeEvent): void => {
    const { value } = e.target;
    setSelectedValue(value);
    onChange?.(...getOption(value));
  };
  useSkipEffectOnMount(() => {
    setSelectedIndex(initialSelectedIndex);
  }, [initialSelectedIndex]);

  useEffect(() => {
    const option = options?.[selectedIndex] ?? undefined;

    setSelectedValue(option ? getValue(option, selectedIndex) : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  return (
    <>
      <RadioGroupContainer
        onChange={updateInternal}
        value={selectedValue}
        selectedOptionColor={selectedOptionColor}
        color={color}
        direction={direction}
        disabled={disabled}
      >
        {options?.map((option, idx) => {
          const value = getValue?.(option, idx);
          const label = getLabel?.(option);
          return renderRadioElement(
            <Radio value={value} key={idx}>
              {label}
            </Radio>,
            option,
            idx
          );
        })}
      </RadioGroupContainer>
      {resetSelectionFnRef ? (
        <UseReset
          resetRef={resetSelectionFnRef}
          selectIndex={setSelectedIndex}
        />
      ) : null}
    </>
  );
};

export default memo(RadioGroup);
